import { uid } from 'quasar';
import { defineStore } from 'pinia';
import type { dynamicObj } from 'src/pages/assets';
import { masterDataApiService } from 'src/services';
import { usePoMatchStore, useSettingStore } from 'src/store/main';
import {
  auto_accounting_setting,
  auto_accounting_types_setting,
  auto_accounting_acc_list_name_setting,
  auto_accounting_po_acc_list_name_setting,
  auto_accounting_dimensions_list_setting,
  auto_accounting_dimensions_options_setting,
  auto_accounting_fields_setting,
} from 'src/constants';
import { hasValue, isUndefinedOrNull, objLen } from 'src/utils/general';

export interface AdditionalField {
  field_name: string;
  [key: string]: unknown;
}

export const useAccountingTypeStore = defineStore('accounting_type_store', {
  state: () => ({
    view_template: '' as string,
    view_types: {
      ln: 'LN_VIEW',
      m3: 'M3_VIEW',
    } as const,
    line_item_loaded: false,
    accounts_promise_resolve: null as ((value: unknown) => void) | null,
    accounting_additional_fields: {} as Record<string, AdditionalField>,
    settings_store: useSettingStore(),
    masterDataLimit: 99999,
  }),
  getters: {},
  actions: {
    resetAccountingTypeStore() {
      this.line_item_loaded = false;
      this.view_template = '';
      this.accounts_promise_resolve = null;
    },

    getbaseTotalLineItem() {
      const line = this.baseLineItem();
      line.type = 'total_line_item';
      line.amount_readonly = true;

      delete line.checked;
      return line;
    },

    baseLineItem() {
      if (this.view_template === 'LN_VIEW') {
        return this.getLNLineItem();
      }
      return this.getM3LineItem();
    },

    baseSplitLineItem() {
      if (this.view_template === 'LN_VIEW') {
        return this.getLNSplitLineItem();
      }
      return this.getM3SplitLineItem();
    },

    getM3LineItem() {
      return {
        id: '',
        type: 'line_item',
        account: '',
        voucher_text: '',
        account_error: '',
        amount: '',
        amount_standard: 0,
        amount_readonly: false,
        checked: '',
        split_items: [],
        amount_error: '',
        position: '',
        dimensions: [],
        readonly: false,
        error: '',
        pending_amount: '0',
        lines_total_amount: '0',
        description: '',
        item_number: '',
      } as dynamicObj;
    },

    getLNLineItem() {
      const line = {
        id: '',
        type: 'line_item',
        account: '',
        account_error: '',
        amount: '',
        amount_standard: 0,
        amount_readonly: false,
        checked: '',
        split_items: [],
        amount_error: '',
        position: '',
        dimensions: [],
        readonly: false,
        error: '',
        pending_amount: '0',
        lines_total_amount: '0',
        description: '',
        item_number: '',
        booking_reference: '',
      } as dynamicObj;

      if (this.hasAdditionalFields()) {
        for (const field of Object.values(
          this.accounting_additional_fields as Record<string, AdditionalField>,
        )) {
          line[field.field_name] = '';
        }
      }
      return line;
    },

    getLNSplitLineItem() {
      const split_line = {
        id: uid(),
        account: '' as string | dynamicObj,
        percentage: '',
        amount: '',
        amount_standard: 0,
        amount_error: '',
        percentage_error: '',
        account_error: '',
        is_validated: true,
        booking_reference: '',
      } as dynamicObj;

      if (this.hasAdditionalFields()) {
        for (const field of Object.values(
          this.accounting_additional_fields as Record<string, AdditionalField>,
        )) {
          split_line[field.field_name] = '';
        }
      }
      return split_line;
    },

    getM3SplitLineItem() {
      return {
        id: uid(),
        account: '' as string | dynamicObj,
        percentage: '',
        amount: '',
        amount_standard: 0,
        amount_error: '',
        percentage_error: '',
        account_error: '',
        is_validated: true,
        voucher_text: '',
      };
    },

    verifyModuleSettings() {
      const errors_list = [] as string[];
      const settings_store = useSettingStore();
      const { user_settings } = settings_store;

      if (!user_settings[auto_accounting_setting]) {
        errors_list.push('auto accounting settings not enabled');
        return errors_list;
      }

      if (user_settings[auto_accounting_types_setting] === 'm3') {
        const po_match_store = usePoMatchStore();
        const { hasPoMatchData } = po_match_store;
        if (
          hasPoMatchData() &&
          !hasValue(user_settings[auto_accounting_po_acc_list_name_setting])
        ) {
          errors_list.push(auto_accounting_po_acc_list_name_setting);
          return errors_list;
        }
      }

      let settings = [auto_accounting_acc_list_name_setting];

      const ln_settings = [
        auto_accounting_dimensions_list_setting,
        auto_accounting_dimensions_options_setting,
      ];
      if (user_settings[auto_accounting_types_setting] === 'ln') {
        settings = settings.concat(ln_settings);
      }

      for (const setting of settings) {
        if (!hasValue(user_settings[setting])) {
          errors_list.push(setting);
        }
      }

      return errors_list;
    },

    // Additional fileds
    getAdditionalFields() {
      const settings_store = useSettingStore();
      const additional_fields =
        settings_store.user_settings[auto_accounting_fields_setting];
      let additional_fields_obj = {} as dynamicObj;
      try {
        if (additional_fields) {
          additional_fields_obj = JSON.parse(additional_fields);
        }
      } catch (error) {}

      return additional_fields_obj;
    },

    hasAdditionalFields() {
      return (
        !isUndefinedOrNull(this.accounting_additional_fields) &&
        objLen(this.accounting_additional_fields) > 0
      );
    },

    resetlineAdditionalFields(item: dynamicObj): dynamicObj {
      if (this.hasAdditionalFields()) {
        for (const field of Object.values(
          this.accounting_additional_fields as Record<string, AdditionalField>,
        )) {
          item[field.field_name] = '';
        }
      }
      return item;
    },

    dynamicAdditionalFieldStyle(): string {
      const fieldCount = objLen(this.accounting_additional_fields);

      // If there are no fields, don't apply any width (or you can hide it)
      if (fieldCount === 0) {
        return 'display: none';
      }

      // If more than 2 fields, divide the inner width equally
      if (fieldCount > 2) {
        return `width: ${100 / fieldCount}%`;
      }

      // If exactly 2 fields, divide the inner width by 50%
      if (fieldCount === 2) {
        return 'width: 50%';
      }

      // If 1 field, use full width
      return 'width: 100%';
    },

    async getMasterData(listName: string) {
      try {
        const request_params = this.prepareRequestData(listName);

        if (!request_params) {
          return [] as dynamicObj[];
        }

        const response = await masterDataApiService.getData_V2(
          request_params,
          '',
        );
        if (!response) {
          throw response;
        }
        return response.data as dynamicObj[];
      } catch (error) {
        throw error;
      }
    },

    prepareRequestData(
      settingName: string,
      includeSortOrder = false,
      limit?: number,
      skip = 0,
    ) {
      const dataType = this.getSettingsName(settingName);

      if (!dataType) {
        return null;
      }

      const request_params = {} as dynamicObj;
      request_params['skip'] = skip;
      request_params['limit'] = limit ?? this.masterDataLimit;
      request_params['data_type'] = dataType;

      if (includeSortOrder) {
        request_params['sort_order_json'] = JSON.stringify([
          { field: 'nominalaccount', direction: 'asc' },
        ]);
      }

      return request_params;
    },

    getSettingsName(settingName: string): string | undefined {
      return this.settings_store.user_settings[settingName];
    },

    // Awaitable delay used for retry backoff.
    delay(ms: number): Promise<void> {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },

    // masterDataApiService.getAccountingDropdowns swallows failures and returns
    // { name, error } instead of throwing, so a 502/network error surfaces here
    // as an `error` field on the result item. On retry only the failed items are
    // re-requested (not the whole batch) and merged back into their original
    // slot by `name`, with exponential backoff between attempts. After the last
    // attempt, if any item is still failing it throws, so the caller's error
    // handler shows it (dropping a list/page silently would mean incomplete
    // account data).
    async getAccountingDropdownsWithRetry(
      apisParamsList: any,
      maxRetries = 3,
    ): Promise<any> {
      const call = (list: any) =>
        masterDataApiService.getAccountingDropdowns(list);

      // Initial attempt for the full batch.
      const results = await call(apisParamsList);

      if (!Array.isArray(results)) {
        return results;
      }

      // Index results by name so we can replace only the failed slots.
      const resultByName = {} as dynamicObj;
      for (const item of results) {
        resultByName[item.name] = item;
      }

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        // Only the params whose result still carries an `error`.
        const failedParams = apisParamsList.filter(
          (p: any) => resultByName[p.name] && resultByName[p.name].error,
        );

        if (failedParams.length === 0) {
          break;
        }

        // 500ms, 1000ms, 2000ms ...
        await this.delay(500 * Math.pow(2, attempt - 1));

        const retryResults = await call(failedParams);

        if (Array.isArray(retryResults)) {
          for (const item of retryResults) {
            resultByName[item.name] = item;
          }
        }
      }

      // Re-assemble in the original order/length.
      const merged = apisParamsList.map((p: any) => resultByName[p.name]);

      const stillFailing = merged.find((item: any) => item && item.error);
      if (stillFailing) {
        throw new Error(
          'Failed to load accounting dropdowns after ' +
            maxRetries +
            ' retries: ' +
            stillFailing.error,
        );
      }

      return merged;
    },

    // Handles pagination for every list. The first response carries
    // `total_records`, so we know up-front how many pages each list has and can
    // request all the remaining pages in parallel (one Promise.all) instead of
    // sequentially. Pages are written back into their exact slot, so the
    // combined order is preserved regardless of which request resolves first.
    // Failed requests (e.g. 502) are retried with backoff via
    // getAccountingDropdownsWithRetry.
    async getAccountingDropdownsPaginated(
      apisParamsList: any,
      settingNameByApi: dynamicObj,
    ): Promise<any> {
      const pageSize = 45000;
      // Max page requests in flight at once during the parallel fan-out.
      const maxConcurrency = 5;

      // First page for every list. Force the page size so page 1 lines up with
      // the skip boundaries of the later pages (the caller's limit may differ).
      const firstPageParamsList = apisParamsList.map((p: any) => {
        const settingName = settingNameByApi[p.name];
        const firstParams = settingName
          ? this.prepareRequestData(settingName, true, pageSize, 0)
          : p.params;
        return { params: firstParams ?? p.params, name: p.name };
      });

      // First page for every list (includes pagination metadata).
      const accountingData =
        await this.getAccountingDropdownsWithRetry(firstPageParamsList);

      if (!accountingData || accountingData.length == 0) {
        return accountingData;
      }

      // Flat list of every remaining page request across all lists.
      // Each entry knows which list it belongs to and its page index so the
      // combined data stays ordered.
      const pageRequests = [] as dynamicObj[];

      for (const item of accountingData) {
        const settingName = settingNameByApi[item.name];

        const firstPage = Array.isArray(item.data) ? item.data : [];

        // Total record count reported by the server (fall back to the first
        // page length when the metadata is missing).
        const totalRecords =
          typeof item.total_records === 'number'
            ? item.total_records
            : firstPage.length;

        const totalPages = Math.ceil(totalRecords / pageSize);

        // pages[0] is the first response; reserve a slot for each later page.
        item.pages_data = [firstPage];

        if (!settingName) {
          continue;
        }

        for (let pageIndex = 1; pageIndex < totalPages; pageIndex++) {
          item.pages_data.push([]);
          pageRequests.push({
            name: item.name,
            settingName,
            item,
            pageIndex,
            skip: pageIndex * pageSize,
          });
        }
      }

      // Fetch the remaining pages with at most `maxConcurrency` requests in
      // flight at once (a shared cursor hands the next page to whichever slot
      // frees up). Each page is written into its reserved slot to keep order.
      if (pageRequests.length > 0) {
        const fetchPage = async (req: dynamicObj) => {
          const params = this.prepareRequestData(
            req.settingName,
            true,
            pageSize,
            req.skip,
          );

          if (!params) {
            return;
          }

          const pageResult = await this.getAccountingDropdownsWithRetry([
            { params, name: req.name },
          ]);

          const pageData =
            pageResult &&
            pageResult.length > 0 &&
            Array.isArray(pageResult[0].data)
              ? pageResult[0].data
              : [];

          // Write into the reserved slot to keep page order intact.
          req.item.pages_data[req.pageIndex] = pageData;
        };

        let cursor = 0;
        const runSlot = async () => {
          while (cursor < pageRequests.length) {
            const req = pageRequests[cursor++];
            await fetchPage(req);
          }
        };

        const slots = Math.min(maxConcurrency, pageRequests.length);
        await Promise.all(Array.from({ length: slots }, () => runSlot()));
      }

      // Flatten each list's ordered pages back into item.data.
      for (const item of accountingData) {
        item.data = (item.pages_data || []).flat();
        delete item.pages_data;
      }

      return accountingData;
    },
  },
});
